import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [cognitoUser, setCognitoUser] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);

  // Check if user is already logged in
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log('User already logged in, redirecting...', user);
        
        // Redirect based on role
        const path = user.role === 'admin' ? '/admin' : '/dashboard';
        navigate(path, { replace: true });
      } catch (err) {
        console.error('Error parsing stored user:', err);
      }
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Import Amplify v6 Auth
      const { signIn } = await import('aws-amplify/auth');
      
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      // Check if user needs to change password
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsPasswordChange(true);
        setCognitoUser(result);
        setLoading(false);
        return;
      }
      
      // If we get here, sign in was successful and user is verified
      // Get user attributes
      const { fetchUserAttributes } = await import('aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      
      // Determine role based on exact email match
      const isAdmin = attributes.email === 'admin@prepsmart.com.au';
      const role = isAdmin ? 'admin' : 'student';
      
      // Store user info in localStorage
      const userData = {
        email: attributes.email,
        name: attributes.name || attributes.family_name || 'Student',
        role: role
      };
      localStorage.setItem('user', JSON.stringify(userData));

      console.log('Login successful, redirecting...', { isAdmin, role });
      
      // Force a full page reload to ensure App.js picks up the new user state
      const path = isAdmin ? '/admin' : '/dashboard';
      window.location.href = path;
    } catch (err) {
      console.error('Login error:', err);
      
      if (err.name === 'UserNotConfirmedException') {
        // User needs to verify email - show verification form on same page
        setNeedsVerification(true);
        setError('');
        setLoading(false);
        return;
      } else if (err.name === 'UserUnAuthenticatedException' && err.message?.includes('User needs to be authenticated')) {
        // This happens when signIn succeeds but user is not confirmed
        // Treat it the same as UserNotConfirmedException
        setNeedsVerification(true);
        setError('');
        setLoading(false);
        return;
      } else if (err.name === 'NotAuthorizedException') {
        setError('Incorrect email or password. Please try again.');
      } else if (err.name === 'UserNotFoundException') {
        setError('No account found with this email. Please register first.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setVerifyingCode(true);

    try {
      const { confirmSignUp, signIn, fetchUserAttributes } = await import('aws-amplify/auth');
      
      // First, verify the email with the code
      await confirmSignUp({
        username: formData.email,
        confirmationCode: verificationCode
      });

      console.log('Email verified successfully, now logging in...');
      
      // Track registration conversion for Google Ads (user verified from login page)
      const { trackRegistration } = await import('../utils/analytics');
      trackRegistration(formData.email);
      
      // Now sign in the user
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      // Get user attributes
      const attributes = await fetchUserAttributes();
      
      // Determine role
      const isAdmin = attributes.email === 'admin@prepsmart.com.au';
      const role = isAdmin ? 'admin' : 'student';
      
      // Store user info
      localStorage.setItem('user', JSON.stringify({
        email: attributes.email,
        name: attributes.name || attributes.family_name || 'Student',
        role: role
      }));

      console.log('Login successful after verification, redirecting...');
      
      // Redirect
      const path = isAdmin ? '/admin' : '/dashboard';
      window.location.href = path;
    } catch (err) {
      console.error('Verification error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please check the code and try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Verification code has expired. Please click "Resend Code" to get a new one.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
      setVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setResendingCode(true);

    try {
      const { resendSignUpCode } = await import('aws-amplify/auth');
      
      await resendSignUpCode({
        username: formData.email
      });

      alert('✅ New verification code sent! Check your email.');
      setError('');
    } catch (err) {
      console.error('Resend code error:', err);
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setResendingCode(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    setLoading(true);
    
    try {
      const { confirmSignIn, fetchUserAttributes } = await import('aws-amplify/auth');
      
      await confirmSignIn({
        challengeResponse: newPassword
      });
      
      // Get user attributes after password change
      const attributes = await fetchUserAttributes();
      
      // Determine role
      const isAdmin = attributes.email === 'admin@prepsmart.com.au';
      const role = isAdmin ? 'admin' : 'student';
      
      // Store user info
      localStorage.setItem('user', JSON.stringify({
        email: attributes.email,
        name: attributes.name || attributes.family_name || 'Student',
        role: role
      }));

      console.log('Password changed successfully, preparing redirect...', { isAdmin, role });
      
      const path = isAdmin ? '/admin' : '/dashboard';
      console.log('About to redirect to:', path);
      
      // Stop loading and force immediate redirect
      setLoading(false);
      
      // Use setTimeout 0 to ensure this runs after current execution
      setTimeout(() => {
        console.log('Executing redirect now to:', path);
        window.location.assign(path);
      }, 0);
      
      return;
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.message || 'Failed to change password. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '500px', margin: '60px auto' }}>
      <div className="card" style={{ padding: '40px' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '64px', marginBottom: '15px' }}>
            {needsVerification ? '📧' : needsPasswordChange ? '🔑' : '🎓'}
          </div>
          <h1 style={{ fontSize: '36px', marginBottom: '10px', color: '#4f46e5' }}>
            {needsVerification ? 'Verify Your Email' : needsPasswordChange ? 'Change Your Password' : 'Welcome Back'}
          </h1>
          <p style={{ color: '#666' }}>
            {needsVerification 
              ? 'Enter the verification code to complete login' 
              : needsPasswordChange 
                ? 'Please set a new password to continue' 
                : 'Login to continue your practice'}
          </p>
        </div>

        {error && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {needsVerification ? (
          // Email Verification Form
          <div>
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #f59e0b',
              borderRadius: '8px', 
              padding: '15px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              <strong>⚠️ Email Verification Required</strong>
              <p style={{ margin: '8px 0 0 0', lineHeight: '1.6' }}>
                Your account needs to be verified before you can login. We sent a verification code to <strong>{formData.email}</strong> when you registered.
              </p>
            </div>

            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #bfdbfe',
              borderRadius: '8px', 
              padding: '15px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#1e40af',
              lineHeight: '1.6'
            }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: '600' }}>
                💡 Verification emails are sent from <strong>noreply@prepsmart.com.au</strong>
              </p>
              <p style={{ margin: 0 }}>
                <strong>Didn't receive the code?</strong> Check your spam folder or contact us at{' '}
                <a href="mailto:prepsmart.help@gmail.com" style={{ color: '#4f46e5', fontWeight: 'bold' }}>
                  prepsmart.help@gmail.com
                </a>
              </p>
            </div>

            <form onSubmit={handleVerifyCode}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '24px',
                    textAlign: 'center',
                    letterSpacing: '8px',
                    fontWeight: 'bold'
                  }}
                  placeholder="000000"
                />
                <small style={{ color: '#666', fontSize: '14px', display: 'block', marginTop: '8px' }}>
                  Enter the 6-digit code from your email
                </small>
              </div>

              <button
                type="submit"
                disabled={verifyingCode || verificationCode.length !== 6}
                className="btn-primary"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '10px',
                  opacity: (verifyingCode || verificationCode.length !== 6) ? 0.6 : 1,
                  cursor: (verifyingCode || verificationCode.length !== 6) ? 'not-allowed' : 'pointer'
                }}
              >
                {verifyingCode ? 'Verifying & Logging In...' : 'Verify & Login'}
              </button>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendingCode}
                className="btn-secondary"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  marginBottom: '10px'
                }}
              >
                {resendingCode ? 'Sending...' : 'Resend Verification Code'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setNeedsVerification(false);
                  setVerificationCode('');
                  setError('');
                }}
                className="btn-secondary"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px'
                }}
              >
                ← Back to Login
              </button>
            </form>
          </div>
        ) : needsPasswordChange ? (
          // Password Change Form
          <form onSubmit={handlePasswordChange}>
            <div style={{ 
              padding: '15px', 
              background: '#fef3c7', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              <strong>⚠️ Temporary Password Detected</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                You're logging in with a temporary password. Please create a new password to secure your account.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="Enter new password"
              />
              <small style={{ color: '#666', fontSize: '13px' }}>
                Min 8 characters, include uppercase, lowercase, and number
              </small>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={8}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                placeholder="Re-enter new password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: 'bold',
                opacity: loading ? 0.6 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Changing Password...' : 'Change Password & Login'}
            </button>
          </form>
        ) : (
          // Regular Login Form
          <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '14px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '16px'
              }}
              placeholder="your.email@example.com"
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '14px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '16px'
              }}
              placeholder="Enter your password"
            />
            <div style={{ textAlign: 'right', marginTop: '8px' }}>
              <a href="/forgot-password" style={{ color: '#4f46e5', fontSize: '14px', textDecoration: 'none' }}>
                Forgot password?
              </a>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '18px',
              fontWeight: 'bold',
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>

          <p style={{ textAlign: 'center', marginTop: '25px', color: '#666' }}>
            Don't have an account?{' '}
            <a href="/register" style={{ color: '#4f46e5', fontWeight: 'bold', textDecoration: 'none' }}>
              Register here
            </a>
          </p>
        </form>
        )}
      </div>

      <div className="card" style={{ marginTop: '20px', padding: '20px', background: '#f0f9ff', textAlign: 'center' }}>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
          🔒 Your login is secure and encrypted
        </p>
      </div>
    </div>
  );
}

export default Login;
